import { createAction, createReducer } from '@reduxjs/toolkit';

import { Loadable, UserManager } from 'models';
import { makeThunkFromAPICall } from 'state';

export type UsersState = {
    users: Loadable<UserManager.UserList>;
    selectedUser: Loadable<UserManager.User>;
    actionResponse: Loadable<UserManager.API.ActionResponse>;
    pageIndex: number;
    pageRows: number;
    searchFor: string;
};

const initialState: UsersState = {
    users: {
        status: 'idle',
    },
    selectedUser: {
        status: 'idle',
    },
    actionResponse: {
        status: 'idle',
    },
    pageIndex: 0,
    pageRows: 5,
    searchFor: '',
};

export const setPageIndex = createAction<number>('admin/users/pageindex');
export const setPageRows = createAction<number>('admin/users/pagerows');
export const setSearchFor = createAction<string>('admin/users/searchfor');

export const [getUsers, setUsers] = makeThunkFromAPICall(
    UserManager.getUsers,
    'admin/users',
);

export const [getUser, setUser] = makeThunkFromAPICall(
    UserManager.getUser,
    'admin/users/user',
);

export const [updateUser, setUpdateUserResponse] = makeThunkFromAPICall(
    UserManager.updateUser,
    'admin/users/updateUser',
);

export const [addNewUser, setAddNewUserResponse] = makeThunkFromAPICall(
    UserManager.addNewUser,
    'admin/users/new',
);

export const [deleteUser, setDeleteUserResponse] = makeThunkFromAPICall(
    UserManager.deleteUser,
    'admin/users/delete',
);

export const usersReducer = createReducer(initialState, (builder) => {
    builder.addCase(setUsers, (state, action) => {
        return {
            ...state,
            users: {
                ...state.users,
                ...action.payload.loadable,
            },
            selectedUser: { status: 'idle' },
        };
    });

    builder.addCase(setSearchFor, (state, action) => {
        return { ...state, searchFor: action.payload };
    });

    builder.addCase(setPageIndex, (state, action) => {
        return { ...state, pageIndex: action.payload };
    });

    builder.addCase(setPageRows, (state, action) => {
        return { ...state, pageRows: action.payload, pageIndex: 0 };
    });

    builder.addCase(setUser, (state, action) => {
        return { ...state, selectedUser: action.payload.loadable };
    });

    builder.addCase(setDeleteUserResponse, (state, action) => {
        return {
            ...state,
            actionResponse: {
                ...state.actionResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setUpdateUserResponse, (state, action) => {
        return {
            ...state,
            actionResponse: {
                ...state.actionResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setAddNewUserResponse, (state, action) => {
        return {
            ...state,
            actionResponse: {
                ...state.actionResponse,
                ...action.payload.loadable,
            },
        };
    });
});

export const UsersActions = {
    getUsers,
    getUser,
    updateUser,
    addNewUser,
    deleteUser,
    setPageIndex,
    setPageRows,
    setSearchFor,
};

export default usersReducer;
